import React, { useRef, useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { Trans, Plural } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { ValidatingInput, ValidationForm, ValidatingTextArea, ValidationSummary } from '@/components/forms/validation';
import { saveCatalogGroup, editCatalogGroup } from '@/services/profile-catalog-groups';
import { CatalogGroup, OptionalId } from '@/types';
import { useMergeState } from '@/utils/hooks';
import { Button, ButtonBar, Checkbox } from '@ygtechnologies/rubus-ui';

interface CatalogGroupEditDialogProps {
    onSave(): void;
    onClose(): void;
    onCancel?(): void;
    group: OptionalId<CatalogGroup> | null;
    show: boolean;
    productIds?: number[];
}

export const CatalogGroupEditDialog: React.FC<CatalogGroupEditDialogProps> = ({
    show,
    group,
    onClose,
    onSave,
    onCancel,
    productIds,
}) => {
    const focusRef = useRef<HTMLInputElement>(null);
    const [saving, setSaving] = useState(false);
    const [updatedGroup, mergeUpdatedGroup, setUpdatedGroup] = useMergeState(group);

    const handleCancelClick = () => {
        setUpdatedGroup(group);

        triggerHandler(onCancel);
        triggerHandler(onClose);
    };

    const handleLoaded = () => {
        if (focusRef.current) {
            focusRef.current.focus();
        }
    };

    const handleOpen = async () => {
        setUpdatedGroup(group);

        handleLoaded();
    };

    const handleAcceptClick = async () => {
        try {
            setSaving(true);
            if (updatedGroup!.id) {
                await editCatalogGroup(updatedGroup as CatalogGroup);
            } else {
                await saveCatalogGroup(updatedGroup!, productIds || null);
            }
            // setUpdatedGroup();
            triggerHandler(onSave);
            triggerHandler(onClose);
        } finally {
            setSaving(false);
        }
    };

    const renderContent = () => {
        if (!updatedGroup) {
            return <LoadingIndicator />;
        }

        return (
            <React.Fragment>
                <div className="form-group">
                    <ValidatingInput
                        className="form-control"
                        ref={focusRef}
                        value={updatedGroup!.name}
                        onChange={(e) => mergeUpdatedGroup({ name: e.target.value })}
                        placeholder={t`Group name`}
                        required
                    />
                </div>

                <div className="form-group">
                    <Checkbox
                        inputSize="md"
                        onChange={() => mergeUpdatedGroup({ hasNoneOption: !updatedGroup!.hasNoneOption })}
                        checked={updatedGroup!.hasNoneOption}>
                        <Trans>
                            Include <i> none </i> option
                        </Trans>
                    </Checkbox>
                </div>
                {updatedGroup!.hasNoneOption && (
                    <>
                        <div className="form-group">
                            <ValidatingInput
                                value={updatedGroup!.noneOptionName}
                                onChange={(e) => mergeUpdatedGroup({ noneOptionName: e.target.value })}
                                required
                                className="form-control"
                                placeholder={t`Title for the empty option`}
                            />
                        </div>

                        <div className="form-group">
                            <ValidatingTextArea
                                value={updatedGroup!.noneOptionDescription}
                                onChange={(e) => mergeUpdatedGroup({ noneOptionDescription: e.target.value })}
                                className="form-control"
                                placeholder={t`Description for the none option`}
                            />
                        </div>
                    </>
                )}
                <ValidationSummary />
            </React.Fragment>
        );
    };

    const isNewGroup = !group || !group.id;
    // if (updatedGroup == null) return null;

    return (
        <Modal isOpen={show} toggle={handleCancelClick} onOpened={handleOpen}>
            <ValidationForm onSubmit={handleAcceptClick}>
                <ModalHeader>
                    {isNewGroup ? (
                        <Plural
                            other={`Group ${productIds?.length} products`}
                            one={`Group 1 product`}
                            value={productIds?.length ?? 0}
                        />
                    ) : (
                        <Trans>Edit group</Trans>
                    )}
                </ModalHeader>
                <ModalBody>{renderContent()}</ModalBody>
                <ButtonBar>
                    <ButtonBar.Right>
                        <Button variant="tertiary" onClick={handleCancelClick} disabled={saving}>
                            <Trans>Cancel</Trans>
                        </Button>
                        <Button type="submit" variant="primary" loading={saving}>
                            {isNewGroup ? <Trans>Create group</Trans> : <Trans>Save</Trans>}
                        </Button>
                    </ButtonBar.Right>
                </ButtonBar>
            </ValidationForm>
        </Modal>
    );
};
